import { useState, useEffect, ReactNode } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import type { AppStore } from '../stores/createStore';
import { hydrate as hydrateAuth } from '../stores/slices/authSlice';
import { handleError, type ErrorCode, type ErrorInfo } from '../utils/error-handler';
import { refreshToken } from '../utils/token-refresh';
import { getHomeLoginUrl } from '../utils/auth-redirect';
import { AuthProvider } from './AuthProvider';
import { TokenRefreshHandler } from './TokenRefreshHandler';

/**
 * tRPC React hooks 和客户端类型
 */
interface TRPCReact {
  Provider: React.ComponentType<{
    client: any;
    queryClient: QueryClient;
    children: ReactNode;
  }>;
  auth: {
    me: {
      useQuery: (input: undefined, options?: { enabled?: boolean; retry?: boolean }) => {
        data: any;
        error: any;
      };
    };
    refresh: {
      useMutation: (options?: {
        onSuccess?: (data: { accessToken: string; refreshToken?: string }) => void;
        onError?: () => void;
      }) => {
        mutate: (input: { refreshToken: string }) => void;
      };
    };
  };
}

interface TRPCClient {
  // tRPC 客户端实例（由 trpc.createClient() 创建，用于 React hooks）
  [key: string]: any;
}

interface TRPCProxyClient {
  // tRPC 代理客户端实例（由 createTRPCProxyClient() 创建，用于直接调用）
  auth: {
    refresh: {
      mutate: (input: { refreshToken: string }) => Promise<{
        accessToken: string;
        refreshToken?: string;
      }>;
    };
  };
}

/**
 * Providers 配置选项
 */
export interface ProvidersConfig {
  /** Redux store 实例 */
  store: AppStore;
  /** tRPC React hooks（包含 Provider 和所有 hooks） */
  trpc: TRPCReact;
  /** tRPC 客户端实例（由 trpc.createClient() 创建，用于 React hooks） */
  trpcClient: TRPCClient;
  /** tRPC 代理客户端实例（由 createTRPCProxyClient() 创建，用于直接调用） */
  trpcProxyClient: TRPCProxyClient;
  /** 登录页路径（未授权时跳转） */
  loginPath?: string;
  /** 是否为 home 应用（home 应用允许未登录用户访问，不强制跳转登录） */
  isHomeApp?: boolean;
  /** 公开路径：UNAUTHORIZED 时处于这些路径不跳转登录（用于 requiresAuth: false 的路由，如 /activities/items/olympic） */
  publicPaths?: string[];
  /** 可选的 UI Provider 组件（如 HeroUIProvider、ThemeProvider 等） */
  uiProvider?: React.ComponentType<{ children: ReactNode }>;
  /** 错误处理回调（用于显示错误提示，由业务模块实现） */
  onError?: (errorInfo: ErrorInfo) => void;
}

/**
 * 创建应用根 Providers
 * 
 * 组合了以下 Provider：
 * - tRPC Provider: API 请求
 * - React Query Provider: 数据缓存
 * - Redux Provider: 全局状态
 * - UI Provider: 可选的 UI 框架 Provider（由业务模块传入）
 * - AuthProvider: 认证服务（用户信息获取 + 跨标签页同步）
 * - TokenRefreshHandler: Token 自动刷新
 */
export function createProviders(config: ProvidersConfig): React.ComponentType<{ children: ReactNode }> {
  const {
    store,
    trpc,
    trpcClient,
    trpcProxyClient,
    loginPath = '/login',
    isHomeApp = false,
    publicPaths,
    uiProvider: UIProvider,
    onError,
  } = config;

  const ProvidersComponent = function Providers({ children: propsChildren }: { children: ReactNode }) {
    const [queryClient] = useState(
      () => {
        /**
         * ✅ 全局错误处理：统一处理所有通用错误
         * 
         * 处理策略：
         * - 网络错误、超时、服务器错误：自动显示错误提示
         * - 未授权错误：先尝试刷新 token，失败后跳转登录页
         * - 权限不足：自动显示错误提示
         * 
         * ⚠️ 重要：业务模块不应该在 mutation 的 onError 中处理这些通用错误
         * 业务模块只应该处理：
         * 1. 验证错误（UNPROCESSABLE_CONTENT）- 需要显示在表单字段上
         * 2. 需要特殊业务逻辑的错误（如 CONFLICT、NOT_FOUND 等）
         * 3. 成功逻辑（onSuccess）
         */
        const handleGlobalError = async (error: unknown) => {
          const errorInfo = handleError(error);

          // ✅ 全局处理的错误码：所有通用错误
          // 不包括：UNPROCESSABLE_CONTENT（验证错误，由业务模块处理）
          const globalErrorCodes: ErrorCode[] = [
            'PARSE_ERROR',
            'BAD_REQUEST',
            'UNAUTHORIZED',
            'FORBIDDEN',
            'NOT_FOUND',
            'METHOD_NOT_SUPPORTED',
            'TIMEOUT',
            'CONFLICT',
            'PRECONDITION_FAILED',
            'PAYLOAD_TOO_LARGE',
            'TOO_MANY_REQUESTS',
            'CLIENT_CLOSED_REQUEST',
            'INTERNAL_SERVER_ERROR', // 包括网络错误
          ];

          if (globalErrorCodes.includes(errorInfo.code)) {
            // ✅ 未授权：先尝试刷新 token
            if (errorInfo.code === 'UNAUTHORIZED' && typeof window !== 'undefined') {
              // ✅ 检查当前路径，如果在登录页或注册页，不处理 UNAUTHORIZED 错误
              // 因为登录页检查 Cookie 失败是正常的（说明未登录），不应该跳转
              const currentPath = window.location.pathname;
              const isAuthPage = currentPath === loginPath || currentPath === '/register';
              
              if (isAuthPage) {
                // 在登录页/注册页，UNAUTHORIZED 错误是正常的（未登录状态），静默处理
                return;
              }

              // ✅ 公开路径（requiresAuth: false）：UNAUTHORIZED 静默处理，不跳转
              const isPublicPath = publicPaths?.some(
                (p) => currentPath === p || currentPath.startsWith(p + '/')
              );
              if (isPublicPath) {
                return;
              }
              
              // ✅ home 应用：允许未登录用户访问，UNAUTHORIZED 错误静默处理，不跳转
              if (isHomeApp) {
                // home 应用的认证检查（如 AuthProvider 检查 Cookie）失败是正常的，静默处理
                return;
              }
              
              const refreshed = await refreshToken(store, trpcProxyClient);
              if (!refreshed) {
                // 刷新失败，显示错误并跳转到 home 模块 SSO 登录页（与 RouteGuard 的 redirectToHomeLogin 一致）
                if (onError) {
                  onError(errorInfo);
                }
                if (typeof setTimeout !== 'undefined') {
                  setTimeout(() => {
                    if (typeof window !== 'undefined') {
                      window.location.href = getHomeLoginUrl(window.location.href);
                    }
                  }, 1500);
                }
              }
              // 如果刷新成功，不显示错误，让请求重试
              return;
            }

            // ✅ 其他通用错误：统一显示错误提示
            if (onError) {
              onError(errorInfo);
            }
          }
          // UNPROCESSABLE_CONTENT（验证错误）由业务模块处理，不在这里处理
        };

        return new QueryClient({
          defaultOptions: {
            queries: {
              staleTime: 5 * 1000,
              refetchOnWindowFocus: false,
              // ✅ 根据错误类型决定是否重试
              retry: (failureCount, error) => {
                const errorInfo = handleError(error);

                // ✅ 客户端错误（4xx）不重试
                const noRetryErrorCodes: ErrorCode[] = [
                  'BAD_REQUEST',
                  'UNAUTHORIZED',
                  'FORBIDDEN',
                  'NOT_FOUND',
                  'CONFLICT',
                  'UNPROCESSABLE_CONTENT',
                  'TOO_MANY_REQUESTS',
                ];

                if (noRetryErrorCodes.includes(errorInfo.code)) {
                  return false; // 不重试
                }

                // ✅ 服务器错误（5xx）和超时错误最多重试2次
                return failureCount < 2;
              },
              retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
            },
          },
          // ✅ 全局错误处理：为所有query和mutation添加错误处理
          queryCache: new QueryCache({
            onError: handleGlobalError,
          }),
          mutationCache: new MutationCache({
            onError: handleGlobalError,
          }),
        });
      }
    );

    // 在客户端完成 hydration 后，从 localStorage 恢复认证状态
    useEffect(() => {
      store.dispatch(hydrateAuth());
    }, [store]);

    const content = (
      <>
        <AuthProvider store={store} trpc={trpc} publicPaths={publicPaths} />
        <TokenRefreshHandler store={store} trpc={trpc} />
        {propsChildren}
      </>
    );

    // 如果提供了 UI Provider，则包裹内容
    const withUIProvider = UIProvider ? (
      <UIProvider>{content}</UIProvider>
    ) : (
      content
    );

    return (
      <trpc.Provider client={trpcClient as any} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <ReduxProvider store={store}>{withUIProvider}</ReduxProvider>
        </QueryClientProvider>
      </trpc.Provider>
    );
  };

  return ProvidersComponent;
}

